// askSmart-ui/src/pages/TOSP/tabs/TaxRollsTab.tsx
import React, { useRef, useState } from "react";
import NewTaxBudgetModal from "../components/NewTaxBudgetModal";
import RollsRegister from "../components/RollsRegister";
import TaxBillUploadModal from "../components/TaxBillUploadModal";
import { formatAmount, formatCurrency } from "../format";
import { useTospData } from "../TospDataContext";
import {
  activeOccupant,
  allocationFor,
  instalmentTotals,
  levyOf,
  levyPenalty,
  nextBudgetPa,
  partBudgetOf,
  partShares,
  RollPartAmounts,
  TaxRollRecord,
  unitBudgetTotal,
  withPartBudget,
  RollPart,
  rollPartLabel,
  rollPartsForUnit,
  penaltyTotal,
  TAX_BILL_LABELS,
  TaxBillType,
  unassignedParts,
  unitPenaltyTotal,
  TaxYear,
  unitLevyTotal,
  unitRollBreakdown,
  Unit,
  newId,
} from "../types";
import { useGridSort } from "../useGridSort";
import { parseNumeric } from "../validation";

// Shared by the header and every row so the columns can't drift apart.
const GRID_COLUMNS = "0.7fr 1.4fr 1.6fr 1fr 1fr 1fr 1fr";

type SortCol =
  | "suite"
  | "tenant"
  | "rolls"
  | "budgetPa"
  | "interim"
  | "final"
  | "totalPa";

/** Money columns. Budget PA is authored here; Interim and Final are the
 *  municipality's, and become read-only once a bill has been ingested. */
type AmountKey = "budgetPa" | "interim" | "final";

const AMOUNT_COLUMNS: { col: AmountKey; label: string }[] = [
  { col: "budgetPa", label: "Budget PA" },
  { col: "interim", label: "Interim" },
  { col: "final", label: "Final" },
];

const COLUMNS: { col: SortCol; label: string }[] = [
  { col: "suite", label: "Suite #" },
  // Admins think in tenants, not suite numbers — this is the column they scan.
  { col: "tenant", label: "Tenant" },
  { col: "rolls", label: "Tax Rolls" },
  ...AMOUNT_COLUMNS,
  { col: "totalPa", label: "Total PA" },
];

/** One unit's tax for the active year — identity from the rolls assigned to
 *  it in the register, amounts from that year. `taxYear` is undefined until a
 *  figure is entered. */
interface RollRow {
  unit: Unit;
  /** Current operating name, or "" when the unit is empty. */
  tenant: string;
  /** Shares rather than whole rolls — half of a split roll counts as one. */
  rolls: RollPart[];
  taxYear?: TaxYear;
  /** Ingested from the bill; null when no bill covers this unit this year, in
   *  which case the hand-entered figure still stands. */
  billed: { interim: number | null; final: number | null };
  /** Penalties and credits for the year, across every bill. */
  penalty: number | null;
  /** Budget PA — summed from the roll shares this unit holds. */
  budget: number | null;
}

/** The register, or one tax year. */
type View = "rolls" | number;

// Tax Rolls — the register first, then one sub-tab per tax year.
//
// "Rolls" carries no year: it's every parcel the municipality bills and which
// unit each one belongs to. The year tabs are that year's amounts — Budget PA
// authored here, Interim and Final from the municipality's bills — for the
// rolls a unit holds.
export default function TaxRollsTab() {
  const { units, saveUnit, taxRolls, saveTaxRolls, tenants } = useTospData();
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  // The bill being uploaded. Picking the file IS the button, so the modal
  // opens onto what the bill contains rather than onto a prompt for it.
  const [billFile, setBillFile] = useState<File | null>(null);
  const billInput = useRef<HTMLInputElement>(null);
  /** Units holding no roll are hidden here — they carry no amounts. */
  const [showUnassigned, setShowUnassigned] = useState(false);
  /** Unit ids whose rolls are unfolded — a budget is argued with roll by roll. */
  const [expanded, setExpanded] = useState<string[]>([]);
  const [budgetEdits, setBudgetEdits] = useState<Record<string, string>>({});
  // In-progress text, keyed "<unitId>:<field>", so a half-typed figure isn't
  // parsed away mid-entry. Committed on blur and dropped from here.
  const [edits, setEdits] = useState<Record<string, string>>({});

  // Counted in parts, not rolls: half a split roll with no unit is as
  // unbudgetable as a roll nobody has touched. Surfaced on the Register tab so
  // it's visible without opening it, and it gates the budget below.
  const unassigned = unassignedParts(taxRolls);
  const unassignedCount = unassigned.length;

  // Every year any unit holds, newest first.
  // A year exists if anything is recorded against it anywhere: a budget struck
  // on a roll share, a bill ingested, or a figure typed on a unit before
  // budgets moved onto the rolls. Reading it from the units alone would have
  // meant writing a record to all 93 of them just to make a tab appear.
  const years = Array.from(
    new Set([
      ...units.flatMap((unit) => (unit.taxYears || []).map((y) => y.year)),
      ...taxRolls.flatMap((roll) => (roll.levies || []).map((levy) => levy.year)),
      ...taxRolls.flatMap((roll) => (roll.budgets || []).map((budget) => budget.year)),
    ]),
  ).sort((a, b) => b - a);

  const latestYear = years.length ? Math.max(...years) : null;

  const isUnbudgeted = (year: number) =>
    !taxRolls.some((roll) => (roll.budgets || []).some((b) => b.year === year)) &&
    units.every((unit) =>
      (unit.taxYears || []).every((y) => y.year !== year || y.budgetPa === null),
    );

  /**
   * The one year "+" would finish rather than start — the newest, and only
   * while it has no budget at all.
   *
   * Deliberately not every unbudgeted year: 2022 through 2024 are open with
   * no Budget PA and never will have one, and offering the earliest of those
   * had "+" defaulting to 2022 instead of next year.
   */
  const backfillYears =
    latestYear !== null && isUnbudgeted(latestYear) ? [latestYear] : [];

  /** Whether the municipality's bill of that kind has been ingested. */
  const hasBill = (year: number, type: TaxBillType) =>
    taxRolls.some((roll) =>
      (roll.levies || []).some((levy) => levy.year === year && levy.type === type),
    );

  /**
   * A year can't be opened past one that isn't finished.
   *
   * Next year's budget is this year's Total PA plus a percentage, and that
   * total isn't known until both bills are in — the Interim is only half the
   * year, and the Final is what restates it. Budgeting off an Interim alone
   * would set every unit at roughly half.
   *
   * Backfilling an open-but-unbudgeted year is exempt: that's finishing a job,
   * not starting a later one.
   */
  const missingBills =
    latestYear === null || backfillYears.length > 0
      ? []
      : (["interim", "final"] as TaxBillType[]).filter((type) => !hasBill(latestYear, type));

  // Opens on the newest year, so the tab keeps landing where the day's work
  // is; the register is one click left. With no years yet, there's nothing to
  // open onto but the register.
  const view: View =
    selectedView === "rolls" ||
    (typeof selectedView === "number" && years.includes(selectedView))
      ? selectedView
      : years[0] ?? "rolls";

  const activeYear = typeof view === "number" ? view : years[0];

  // Driven by the units, not by the tax-year records — so a unit added after a
  // year was opened still appears, with blank amounts, rather than silently
  // missing from the roll.
  const allRows: RollRow[] = units.map((unit) => ({
    unit,
    // Whoever holds it now. On a closed year that isn't necessarily who held
    // it then — the column is a way to find the row, not a billing fact.
    tenant: activeOccupant(unit.id, tenants)?.operatingName || "",
    rolls: rollPartsForUnit(taxRolls, unit.id),
    taxYear: (unit.taxYears || []).find((y) => y.year === activeYear),
    billed: {
      interim: unitLevyTotal(taxRolls, unit.id, activeYear, "interim", units),
      final: unitLevyTotal(taxRolls, unit.id, activeYear, "final", units),
    },
    penalty: unitPenaltyTotal(taxRolls, unit.id, activeYear, units),
    // The sum of the shares this unit holds. Null when none of them carry a
    // budget, which lets a hand-typed unit figure still stand.
    budget: unitBudgetTotal(taxRolls, unit.id, activeYear),
  }));

  const rows = showUnassigned ? allRows : allRows.filter((row) => row.rolls.length > 0);
  const hiddenCount = allRows.length - rows.length;

  /** What the town is owed on each date, across every roll it bills — the two
   *  land parcels and the lockers included, since they're paid all the same. */
  const instalments = instalmentTotals(taxRolls, activeYear);

  /** How much of the year's cost is late-payment charge rather than tax. It's
   *  already shared into each unit's Total PA; this is just the portfolio view. */
  const penalties = penaltyTotal(taxRolls, activeYear);

  // Interim and Final come from the bill once one has been ingested; until
  // then whatever was hand-entered still stands, so nothing already keyed is
  // lost by the changeover.
  const amountOf = (row: RollRow, field: AmountKey): number | null => {
    // Budget PA is struck per roll share now; the unit's own figure is only
    // what someone typed before that, or for a unit holding no rolls at all.
    if (field === "budgetPa") return row.budget ?? row.taxYear?.budgetPa ?? null;
    return row.billed[field] ?? row.taxYear?.[field] ?? null;
  };

  /**
   * The whole cost of the year for this unit — the two levies plus whatever
   * the town added for late payment, less any credit.
   *
   * The penalty belongs here rather than nowhere: it's real money the town
   * charged, and a tenant base that only bears the levies leaves it with the
   * landlord.
   */
  const totalPaOf = (row: RollRow): number =>
    (amountOf(row, "interim") ?? 0) + (amountOf(row, "final") ?? 0) + (row.penalty ?? 0);

  const { sortButton, sortRows } = useGridSort<SortCol, RollRow>("suite", (row, col) => {
    switch (col) {
      case "suite":
        return row.unit.suiteNumber || "";
      case "tenant":
        // Vacant units sort together at the end rather than mixing into the A's.
        return row.tenant || "￿";
      case "rolls":
        // Sorts by the first roll, so a unit's block stays where the bill puts
        // it; units with none sort together at the end.
        return row.rolls[0]?.roll.rollNumber || "￿";
      case "totalPa":
        return totalPaOf(row);
      case "interim":
      case "final":
        return amountOf(row, col) ?? -Infinity;
      default:
        // Blank amounts sort together rather than being read as text.
        return row.taxYear?.[col as AmountKey] ?? -Infinity;
    }
  });

  // Only the unit that owns the roll is written — the rest are untouched, so
  // two admins typing into different units never contend. The year record is
  // created on first entry, so nothing has to be opened in advance.
  const commit = async (unit: Unit, field: AmountKey, raw: string) => {
    const value = raw.trim() ? parseNumeric(raw) : null;
    const existing = unit.taxYears || [];
    const current = existing.find((y) => y.year === activeYear);
    if (current ? current[field] === value : value === null) {
      // Nothing changed — don't spend a write on a blur.
      setEdits((prev) => {
        const next = { ...prev };
        delete next[`${unit.id}:${field}`];
        return next;
      });
      return;
    }

    const taxYears: TaxYear[] = current
      ? existing.map((y) => (y.year === activeYear ? { ...y, [field]: value } : y))
      : [
          {
            id: newId("tyr"),
            year: activeYear,
            budgetPa: null,
            interim: null,
            final: null,
            [field]: value,
          } as TaxYear,
          ...existing,
        ];

    try {
      await saveUnit({ ...unit, taxYears });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[`${unit.id}:${field}`];
        return next;
      });
    } catch {
      /* surfaced by the provider; the typed value stays in the box */
    }
  };

  /**
   * What one roll share cost in the most recent year it was billed before
   * `before` — the figure next year's budget is struck from.
   *
   * Levies and penalties are the parcel's; the share is this part's slice of
   * it, taken as the division stood in the year being read.
   */
  const partPriorTotal = (
    roll: TaxRollRecord,
    partId: string,
    before: number,
  ): number | null => {
    const billed = [...new Set((roll.levies || []).map((levy) => levy.year))]
      .filter((year) => year < before)
      .sort((a, b) => b - a);
    if (billed.length === 0) return null;
    const year = billed[0];

    const shares = partShares(allocationFor(roll, year), units);
    // A part id belongs to one allocation; if the year in question divided the
    // roll differently, fall back to the whole so nothing is budgeted at zero.
    const share = shares?.find((s) => s.part.id === partId)?.share ?? null;
    if (share === null) return null;

    const types = [
      ...new Set((roll.levies || []).filter((l) => l.year === year).map((l) => l.type)),
    ];
    const levies = types.reduce((sum, type) => sum + (levyOf(roll, year, type)?.levy ?? 0), 0);
    const penalties = types.reduce((sum, type) => sum + levyPenalty(roll, year, type), 0);
    return (levies + penalties) * share;
  };

  /**
   * Opens the year across the whole portfolio.
   *
   * Budgets are struck per roll share, not per unit: a budget is last year's
   * bill for that parcel plus a percentage, and a unit holding ten parcels is
   * ten separate figures an admin can argue with individually. The unit's
   * Budget PA is their sum.
   *
   * Only the rolls are written. Opening a year used to also put a record on
   * every unit, and because saving a unit rewrites all of its years that was
   * three round trips per unit on top of the rolls — minutes of writes for a
   * tab that the roll budgets already imply.
   */
  const createTaxYear = async (year: number, percentageIncrease: number) => {
    try {
      const changed: TaxRollRecord[] = [];
      for (const roll of taxRolls) {
        let next = roll;
        let dirty = false;
        for (const part of allocationFor(roll, year).parts) {
          // Never overwrite a figure already struck for this year.
          if (partBudgetOf(roll, year, part.id) !== null) continue;
          const previous = partPriorTotal(roll, part.id, year);
          if (previous === null) continue;
          next = withPartBudget(
            next,
            year,
            part.id,
            nextBudgetPa(previous, percentageIncrease),
          );
          dirty = true;
        }
        if (dirty) changed.push(next);
      }
      if (changed.length > 0) await saveTaxRolls(changed);

      setSelectedView(year);
      setBudgetModalOpen(false);
    } catch {
      /* surfaced by the provider */
    }
  };

  /** In-progress budget text, keyed "<rollId>:<partId>". */
  const budgetKey = (detail: RollPartAmounts) => `${detail.roll.id}:${detail.part.id}`;

  const commitBudget = async (detail: RollPartAmounts, raw: string) => {
    const value = raw.trim() ? parseNumeric(raw) : null;
    const clear = () =>
      setBudgetEdits((prev) => {
        const next = { ...prev };
        delete next[budgetKey(detail)];
        return next;
      });

    if (value === detail.budget) {
      // Nothing changed — don't spend a write on a blur.
      clear();
      return;
    }
    try {
      await saveTaxRolls([
        withPartBudget(detail.roll, activeYear, detail.part.id, value),
      ]);
      clear();
    } catch {
      /* surfaced by the provider; the typed value stays in the box */
    }
  };

  /**
   * Sets Budget PA from the unit row.
   *
   * Only reached when the unit holds exactly one roll share, where the unit's
   * figure and the roll's are the same figure. A unit holding several is read
   * on this row and edited in the expander — a total is the sum of its parts,
   * and letting it be typed would mean inventing an apportionment nobody
   * asked for.
   */
  const commitUnitBudget = async (row: RollRow, raw: string) => {
    const clear = () =>
      setEdits((prev) => {
        const next = { ...prev };
        delete next[`${row.unit.id}:budgetPa`];
        return next;
      });

    const [only] = unitRollBreakdown(taxRolls, row.unit.id, activeYear, units);
    // No roll to put it on — the unit's own figure is all there is.
    if (!only) {
      await commit(row.unit, "budgetPa", raw);
      return;
    }
    await commitBudget(only, raw);
    clear();
  };

  const toggleExpanded = (unitId: string) =>
    setExpanded((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId],
    );

  const amountCell = (row: RollRow, field: AmountKey, label: string) => {
    // Budget PA adds up the shares this unit holds, but it stays typeable:
    // most of the time an admin wants to set the unit's number and not think
    // about ten parcels. Doing it here rescales the parts to match, so the
    // total and its breakdown can't drift apart. Unfold the row to set one
    // parcel on its own.
    // More than one roll: the total is the sum of its parts and nothing else,
    // so it reads. Editing it would mean deciding how to spread a figure the
    // admin hasn't apportioned — and the parts are each editable in the
    // expander, which is where that decision actually belongs.
    if (field === "budgetPa" && row.rolls.length > 1) {
      return (
        <div
          className="tosp-calc-value"
          title={`${row.rolls.length} rolls added up — open the row to change one`}
        >
          {row.budget === null ? "—" : formatCurrency(row.budget)}
        </div>
      );
    }

    // A single roll is the unit, so its budget can be typed here directly.
    if (field === "budgetPa" && row.rolls.length === 1) {
      const key = `${row.unit.id}:budgetPa`;
      return (
        <div className="tosp-affix-wrap">
          <span className="tosp-affix tosp-affix-prefix">$</span>
          <input
            className="manage-input tosp-has-prefix"
            autoComplete="off"
            inputMode="decimal"
            aria-label={`${label} for suite ${row.unit.suiteNumber}`}
            value={edits[key] ?? (row.budget === null ? "" : formatAmount(row.budget))}
            // Grouped while it sits there, plain the moment it's touched —
            // regrouping digits under the cursor moves the cursor.
            onFocus={() =>
              setEdits((prev) => ({
                ...prev,
                [key]: row.budget === null ? "" : String(Math.round(row.budget * 100) / 100),
              }))
            }
            onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
            onBlur={(e) => commitUnitBudget(row, e.target.value)}
          />
        </div>
      );
    }

    // An ingested figure is the municipality's, summed across the unit's rolls
    // — it isn't ours to type over, so it reads rather than edits.
    const billed = field === "budgetPa" ? null : row.billed[field];
    if (billed !== null) {
      return (
        <div
          className="tosp-calc-value"
          title={`From the ${activeYear} bill — ${row.rolls.length} roll${
            row.rolls.length === 1 ? "" : "s"
          }`}
        >
          {formatCurrency(billed)}
        </div>
      );
    }

    const key = `${row.unit.id}:${field}`;
    const stored = row.taxYear?.[field];
    return (
      <div className="tosp-affix-wrap">
        <span className="tosp-affix tosp-affix-prefix">$</span>
        <input
          className="manage-input tosp-has-prefix"
          autoComplete="off"
          inputMode="decimal"
          aria-label={`${label} for suite ${row.unit.suiteNumber}`}
          value={
            edits[key] ??
            (stored === null || stored === undefined ? "" : formatAmount(stored))
          }
          onFocus={() =>
            setEdits((prev) => ({
              ...prev,
              [key]: stored === null || stored === undefined ? "" : String(stored),
            }))
          }
          onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
          onBlur={(e) => commit(row.unit, field, e.target.value)}
        />
      </div>
    );
  };

  return (
    <section className="tosp-card">
      {/* One tab per tax year, newest first, then "+" to open the next year's
          budget — and the Register last, set apart, since it carries no year. */}
      <div className="tosp-year-tabs" role="tablist" aria-label="Tax years and roll register">
        {years.map((year) => (
          <button
            key={year}
            type="button"
            role="tab"
            aria-selected={view === year}
            className={`tosp-year-tab${view === year ? " tosp-year-tab-active" : ""}`}
            onClick={() => setSelectedView(year)}
          >
            {year}
          </button>
        ))}
        {/* A budget can't be struck while any tax has nobody to bear it — an
            unassigned part would simply be left out of every unit's share. */}
        <button
          type="button"
          className="tosp-year-tab tosp-year-tab-plus"
          onClick={() => setBudgetModalOpen(true)}
          disabled={units.length === 0 || unassignedCount > 0 || missingBills.length > 0}
          title={
            units.length === 0
              ? "Add a unit first"
              : missingBills.length > 0
                ? `${latestYear} has no ${missingBills
                    .map((type) => TAX_BILL_LABELS[type])
                    .join(" or ")} bill yet — next year's budget is built on ${latestYear}'s Total PA, which isn't known until both are in`
                : unassignedCount > 0
                  ? `${unassignedCount} unassigned — assign them on the Register before budgeting`
                  : "New Tax Budget"
          }
        >
          +
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={view === "rolls"}
          className={`tosp-year-tab tosp-year-tab-register${
            view === "rolls" ? " tosp-year-tab-active" : ""
          }`}
          onClick={() => setSelectedView("rolls")}
          title="Every roll the municipality bills, and the unit each belongs to"
        >
          Register
          {unassignedCount > 0 && (
            <span className="tosp-year-tab-badge">{unassignedCount}</span>
          )}
        </button>
      </div>

      {view === "rolls" ? (
        <RollsRegister />
      ) : units.length === 0 ? (
        <div className="tosp-grid-empty">
          No units yet. Add units on the Units tab, then attach their rolls from the Rolls
          tab's register.
        </div>
      ) : years.length === 0 ? (
        <div className="tosp-grid-empty">No tax years yet. Use + to open one.</div>
      ) : (
        <>
          {unassignedCount > 0 && (
            <p className="tosp-ingest-flag tosp-ingest-flag-warn">
              {unassignedCount} roll{unassignedCount === 1 ? "" : " parts"} still unassigned —
              a new budget can't be opened until every one has a unit. Bills can still be
              uploaded; the cost is held on the roll until it's placed.
            </p>
          )}

          <div className="tosp-toolbar" style={{ justifyContent: "flex-end", gap: 16 }}>
            {/* Off by default: a unit with no rolls has nothing to show in any
                money column, so it's noise on a screen about amounts. The
                Register is where rolls get placed, and its badge is what says
                work is outstanding. */}
            <label className="tosp-rolls-toggle">
              <input
                type="checkbox"
                checked={showUnassigned}
                onChange={(e) => setShowUnassigned(e.target.checked)}
              />
              Show Unassigned
              {hiddenCount > 0 && (
                <span className="tosp-rolls-toggle-count">{hiddenCount}</span>
              )}
            </label>

            <input
              ref={billInput}
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={(e) => {
                setBillFile(e.target.files?.[0] || null);
                // Cleared so choosing the same file twice still fires — after a
                // cancelled upload it's the most likely next pick.
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="tosp-btn-blue"
              onClick={() => billInput.current?.click()}
            >
              Upload Bill
            </button>
          </div>

          <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
            {COLUMNS.map(({ col, label }) => (
              <div key={col}>{sortButton(col, label)}</div>
            ))}
          </div>

          {rows.length === 0 && (
            <div className="tosp-grid-empty">
              No unit holds a tax roll yet. Assign them on the Register, or tick Show
              Unassigned to list every unit.
            </div>
          )}

          {sortRows(rows).map((row) => {
            const open = expanded.includes(row.unit.id);
            return (
              <React.Fragment key={row.unit.id}>
                <div className="tosp-grid-row" style={{ gridTemplateColumns: GRID_COLUMNS }}>
                  <div>{row.unit.suiteNumber}</div>
                  <div>
                    {row.tenant || <span className="tosp-roll-unassigned">Vacant</span>}
                  </div>
                  {/* A unit can hold many rolls — C1 has ten — so the cell
                      summarises them and unfolds into one line each. Split
                      rolls are named with their part, since half of 16701 is a
                      different thing from the whole of it. */}
                  <div>
                    {row.rolls.length === 0 ? (
                      <span className="tosp-roll-unassigned">None assigned</span>
                    ) : row.rolls.length === 1 ? (
                      rollPartLabel(row.rolls[0])
                    ) : (
                      <button
                        type="button"
                        className="tosp-rolls-expander"
                        aria-expanded={open}
                        title={open ? "Hide the rolls" : "List the rolls and what each cost"}
                        onClick={() => toggleExpanded(row.unit.id)}
                      >
                        <span className="tosp-rolls-caret" aria-hidden="true">
                          {open ? "▾" : "▸"}
                        </span>
                        {row.rolls.length} rolls · Plan {row.rolls[0].roll.planNumber} Units{" "}
                        {row.rolls[0].roll.unit}–{row.rolls[row.rolls.length - 1].roll.unit}
                      </button>
                    )}
                  </div>
                  {AMOUNT_COLUMNS.map(({ col, label }) => (
                    <div key={col}>{amountCell(row, col, label)}</div>
                  ))}
                  {/* Total PA is what was billed, never typed — the two levies
                      plus this unit's share of any penalty. */}
                  <div
                    className="tosp-calc-value"
                    title={
                      row.penalty
                        ? `Interim + Final + ${formatCurrency(row.penalty)} penalty — calculated`
                        : "Interim + Final — calculated"
                    }
                  >
                    {formatCurrency(totalPaOf(row))}
                  </div>
                </div>

                {/* One line per roll, on the same columns as the row above, so
                    the parts read straight down against the total they make
                    up. Budget PA is left blank: it's the unit's figure, not
                    something the municipality states per parcel. */}
                {open &&
                  unitRollBreakdown(taxRolls, row.unit.id, activeYear, units).map(
                    (detail, index) => (
                      <div
                        key={`${detail.roll.id}:${detail.part.id}`}
                        className={`tosp-grid-row tosp-roll-detail${
                          index % 2 ? " tosp-roll-detail-alt" : ""
                        }`}
                        style={{ gridTemplateColumns: GRID_COLUMNS }}
                      >
                        <div />
                        <div />
                        <div>
                          {rollPartLabel(detail)}
                          {detail.share < 1 && (
                            <span className="tosp-roll-share">
                              {(detail.share * 100).toFixed(1)}%
                            </span>
                          )}
                        </div>
                        {/* The one figure on this line that's ours to set. */}
                        <div className="tosp-affix-wrap">
                          <span className="tosp-affix tosp-affix-prefix">$</span>
                          <input
                            className="manage-input tosp-has-prefix"
                            autoComplete="off"
                            inputMode="decimal"
                            aria-label={`Budget PA for roll ${rollPartLabel(detail)}`}
                            value={
                              budgetEdits[budgetKey(detail)] ??
                              (detail.budget === null ? "" : formatAmount(detail.budget))
                            }
                            onFocus={() =>
                              setBudgetEdits((prev) => ({
                                ...prev,
                                [budgetKey(detail)]:
                                  detail.budget === null ? "" : String(detail.budget),
                              }))
                            }
                            onChange={(e) =>
                              setBudgetEdits((prev) => ({
                                ...prev,
                                [budgetKey(detail)]: e.target.value,
                              }))
                            }
                            onBlur={(e) => commitBudget(detail, e.target.value)}
                          />
                        </div>
                        <div>
                          {detail.interim === null ? "—" : formatCurrency(detail.interim)}
                        </div>
                        <div>{detail.final === null ? "—" : formatCurrency(detail.final)}</div>
                        <div
                          title={
                            detail.penalty
                              ? `Includes ${formatCurrency(detail.penalty)} penalty`
                              : undefined
                          }
                        >
                          {formatCurrency(detail.total)}
                        </div>
                      </div>
                    ),
                  )}
              </React.Fragment>
            );
          })}

          {/* What actually leaves the bank, and when. Deliberately not a total
              of the column above it: it covers every roll the town bills —
              including the land parcels and lockers that have no unit — and
              each instalment is as the town presented it, so an unpaid earlier
              instalment appears again inside a later one. */}
          {instalments.length > 0 && (
            <div className="tosp-instalment-summary">
              <div className="tosp-instalment-label">
                Due to Town of the Blue Mountains
                <span>all {taxRolls.length} rolls, as billed</span>
              </div>
              <div className="tosp-instalment-dates">
                {instalments.map(({ dueDate, amount }) => (
                  <div key={dueDate} className="tosp-instalment-date">
                    <span>{dueDate}</span>
                    <strong>{formatCurrency(amount)}</strong>
                  </div>
                ))}
                <div className="tosp-instalment-date tosp-instalment-total">
                  <span>Year</span>
                  <strong>
                    {formatCurrency(
                      instalments.reduce((total, i) => total + i.amount, 0),
                    )}
                  </strong>
                </div>
                {/* How much of that is late-payment charge rather than tax.
                    Already shared across the tenants above — shown here so it
                    can be seen, not so it has to be allocated again. */}
                {penalties !== 0 && (
                  <div
                    className="tosp-instalment-date tosp-instalment-penalty"
                    title="Late-payment charges and credits, already included in each unit's Total PA"
                  >
                    <span>Including penalties of</span>
                    <strong>{formatCurrency(penalties)}</strong>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <NewTaxBudgetModal
        isOpen={budgetModalOpen}
        existingYears={years}
        unbudgetedYears={backfillYears}
        onClose={() => setBudgetModalOpen(false)}
        onConfirm={createTaxYear}
      />

      <TaxBillUploadModal
        file={billFile}
        activeYear={activeYear}
        register={taxRolls}
        onClose={() => setBillFile(null)}
        onApply={async (rolls) => {
          await saveTaxRolls(rolls);
        }}
      />
    </section>
  );
}
